import React, { useEffect, useMemo, useState } from 'react';
import { TrainingApi, SpeedReadingLesson, CreateLessonPayload, SpeedReadingVisualMaterial } from './trainingApi';

interface LessonAdminDialogProps {
  initialLesson?: SpeedReadingLesson | null;
  courseId?: string | null;
  onClose: () => void;
  onSaved: (lesson: SpeedReadingLesson) => void;
}

const VOICE_OPTIONS = [
  { value: 'Kore', label: 'Kore — Eğitmen, sıcak ve net' },
  { value: 'Puck', label: 'Puck — Enerjik ve dinamik' },
  { value: 'Charon', label: 'Charon — Sakin ve derin' },
  { value: 'Callirrhoe', label: 'Callirrhoe — Doğal sohbet havası' },
];

type VisualMaterialDraft = SpeedReadingVisualMaterial & {
  draftId: string;
  previewUrl: string;
};

type VisualCategory = 'reading' | 'question' | 'memory_pegs' | 'number_tracking' | 'word_alternatives' | 'horizontal_zigzag' | 'three_column_grid' | 'balloon_reading' | 'star_reading' | 'gap_reading' | 'measurement_text';

const createDraftId = () => `visual_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const rotateFileByDeg = (source: File | string, deg: number, filename: string): Promise<File> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    let objectUrl: string | null = null;
    img.onload = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      const swap = deg === 90 || deg === 270;
      const canvas = document.createElement('canvas');
      canvas.width = swap ? img.naturalHeight : img.naturalWidth;
      canvas.height = swap ? img.naturalWidth : img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas yok')); return; }
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((deg * Math.PI) / 180);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Blob oluşturulamadı')); return; }
        resolve(new File([blob], filename.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.92);
    };
    img.onerror = () => { if (objectUrl) URL.revokeObjectURL(objectUrl); reject(new Error('Görsel yüklenemedi')); };
    if (source instanceof File) {
      objectUrl = URL.createObjectURL(source);
      img.src = objectUrl;
    } else {
      img.crossOrigin = 'anonymous';
      img.src = source;
    }
  });

const fileNameToTitle = (fileName: string) => fileName.replace(/\.[^.]+$/, '').trim() || 'Ders görseli';

const getVisualCategory = (material: Partial<SpeedReadingVisualMaterial>): VisualCategory => {
  if (material.visualCategory === 'question' || material.isQuestionVisual) return 'question';
  if (material.visualCategory === 'memory_pegs') return 'memory_pegs';
  if (material.visualCategory === 'number_tracking') return 'number_tracking';
  if (material.visualCategory === 'word_alternatives') return 'word_alternatives';
  if (material.visualCategory === 'horizontal_zigzag') return 'horizontal_zigzag';
  if (material.visualCategory === 'three_column_grid') return 'three_column_grid';
  if (material.visualCategory === 'balloon_reading') return 'balloon_reading';
  if (material.visualCategory === 'star_reading') return 'star_reading';
  if (material.visualCategory === 'gap_reading') return 'gap_reading';
  if (material.visualCategory === 'measurement_text') return 'measurement_text';
  return 'reading';
};

const getMeasurementTextRole = (material: Partial<SpeedReadingVisualMaterial>) => (
  material.measurementTextRole === 'question' ? 'question' : 'reading'
);

const normalizeCorrectAnswers = (questionCount: number, optionCount: number, values?: Array<number | null>) => (
  Array.from({ length: questionCount }).map((_, index) => {
    const value = Number(values?.[index]);
    return Number.isInteger(value) && value >= 0 && value < optionCount ? value : null;
  })
);

const inferInitialAskSegmentQuizzes = (lesson?: SpeedReadingLesson | null): boolean => {
  const value = lesson?.metadata
    ? (lesson.metadata as Record<string, unknown>).askSegmentQuizzes
    : undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return ['true', '1', 'evet', 'yes'].includes(value.toLowerCase());
  const hasGeneratedQuizzes = Boolean(
    lesson?.metadata?.lessonSegments?.some((segment) => segment.quiz) ||
    (lesson?.metadata?.interactionQuizzes?.length || 0) > 0
  );
  return hasGeneratedQuizzes;
};

const LessonAdminDialog: React.FC<LessonAdminDialogProps> = ({ initialLesson, courseId, onClose, onSaved }) => {
  const isEdit = Boolean(initialLesson);
  const [title, setTitle] = useState(initialLesson?.title || '');
  const [description, setDescription] = useState(initialLesson?.description || '');
  const [voice, setVoice] = useState(initialLesson?.voice || 'Kore');
  const [askSegmentQuizzes, setAskSegmentQuizzes] = useState(() => inferInitialAskSegmentQuizzes(initialLesson));
  const [transcript, setTranscript] = useState(initialLesson?.rawTranscript || '');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(initialLesson?.coverMedia?.url || null);
  const [visualMaterials, setVisualMaterials] = useState<VisualMaterialDraft[]>(() => {
    const materials = Array.isArray(initialLesson?.metadata?.visualMaterials) ? (initialLesson?.metadata?.visualMaterials || []) : [];
    return [...materials]
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((material) => ({
        ...material,
        draftId: material.id || createDraftId(),
        previewUrl: material.media?.url || '',
        file: null,
        visualCategory: getVisualCategory(material),
        measurementTextRole: getMeasurementTextRole(material),
        isQuestionVisual: getVisualCategory(material) === 'question',
        trackTime: getVisualCategory(material) === 'reading' ? material.trackTime !== false : false,
        questionCount: material.questionCount || 4,
        optionCount: material.optionCount || 4,
        correctAnswers: normalizeCorrectAnswers(material.questionCount || 4, material.optionCount || 4, material.correctAnswers),
        memoryPegCount: material.memoryPegCount || 0,
        memoryPegLabels: material.memoryPegLabels || [],
        numberTrackingCount: material.numberTrackingCount || 0,
        numberTrackingDurationSec: material.numberTrackingDurationSec || 60,
        numberTrackingLineXPercent: material.numberTrackingLineXPercent ?? 50,
        numberTrackingItems: material.numberTrackingItems || [],
        balloonReadingItems: material.balloonReadingItems || [],
        balloonReadingDurationSec: material.balloonReadingDurationSec || 180,
        starReadingDurationSec: material.starReadingDurationSec || 120,
        eyeExerciseMode: material.eyeExerciseMode || '',
        eyeExerciseDurationSec: material.eyeExerciseDurationSec || 0,
        metronomeBpm: material.metronomeBpm || 0,
      }));
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rotatingIds, setRotatingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!coverFile) return;
    const url = URL.createObjectURL(coverFile);
    setCoverPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [coverFile]);

  const transcriptLength = useMemo(() => transcript.trim().length, [transcript]);

  const handleAddVisualFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const nextItems = Array.from(files).map((file, index) => {
      const title = fileNameToTitle(file.name);
      return {
        draftId: createDraftId(),
        title,
        pageLabel: title,
        note: '',
        order: visualMaterials.length + index,
        media: null,
        file,
        previewUrl: URL.createObjectURL(file),
        visualCategory: 'reading',
        measurementTextRole: 'reading',
        isQuestionVisual: false,
        trackTime: true,
        questionCount: 4,
        optionCount: 4,
        correctAnswers: normalizeCorrectAnswers(4, 4),
        memoryPegCount: 0,
        memoryPegLabels: [],
        numberTrackingCount: 0,
        numberTrackingDurationSec: 60,
        numberTrackingLineXPercent: 50,
        numberTrackingItems: [],
        balloonReadingItems: [],
        balloonReadingDurationSec: 180,
        starReadingDurationSec: 120,
        eyeExerciseMode: '',
        eyeExerciseDurationSec: 0,
        metronomeBpm: 0,
      };
    });
    setVisualMaterials((prev) => [...prev, ...nextItems]);
  };

  const handleAddThreeColumnGridExercise = () => {
    setVisualMaterials((prev) => ([
      ...prev,
      {
        draftId: createDraftId(),
        title: '3 Sütun Grid',
        pageLabel: '3 Sütun Grid',
        note: 'Metronomla yapılan iki dakikalık 3×3 nokta grid göz egzersizi.',
        order: prev.length,
        media: null,
        file: null,
        previewUrl: '',
        visualCategory: 'three_column_grid',
        isQuestionVisual: false,
        trackTime: false,
        questionCount: 0,
        optionCount: 0,
        correctAnswers: [],
        memoryPegCount: 0,
        memoryPegLabels: [],
        numberTrackingCount: 0,
        numberTrackingDurationSec: 60,
        numberTrackingLineXPercent: 50,
        numberTrackingItems: [],
        balloonReadingItems: [],
        balloonReadingDurationSec: 180,
        starReadingDurationSec: 120,
        eyeExerciseMode: 'three_column_grid',
        eyeExerciseDurationSec: 120,
        metronomeBpm: 120,
      },
    ]));
  };

  const handleAddHorizontalZigzagExercise = () => {
    setVisualMaterials((prev) => ([
      ...prev,
      {
        draftId: createDraftId(),
        title: 'Yatay Zikzak',
        pageLabel: 'Yatay Zikzak',
        note: 'Metronomla yapılan iki dakikalık yatay zikzak göz egzersizi.',
        order: prev.length,
        media: null,
        file: null,
        previewUrl: '',
        visualCategory: 'horizontal_zigzag',
        isQuestionVisual: false,
        trackTime: false,
        questionCount: 0,
        optionCount: 0,
        correctAnswers: [],
        memoryPegCount: 0,
        memoryPegLabels: [],
        numberTrackingCount: 0,
        numberTrackingDurationSec: 60,
        numberTrackingLineXPercent: 50,
        numberTrackingItems: [],
        balloonReadingItems: [],
        balloonReadingDurationSec: 180,
        starReadingDurationSec: 120,
        eyeExerciseMode: 'horizontal_zigzag',
        eyeExerciseDurationSec: 120,
        metronomeBpm: 120,
      },
    ]));
  };

  const renderQuestionAnswerEditor = (material: VisualMaterialDraft) => (
    <div className="mt-3 grid gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#8a9ba6]">Soru sayısı</label>
          <input
            type="number"
            min={1}
            max={30}
            value={material.questionCount || 1}
            onChange={(event) => {
              const questionCount = Math.max(1, Math.min(30, Number(event.target.value) || 1));
              const optionCount = material.optionCount || 4;
              updateVisualMaterial(material.draftId, {
                questionCount,
                correctAnswers: normalizeCorrectAnswers(questionCount, optionCount, material.correctAnswers),
              });
            }}
            className="mt-1 w-full rounded-xl border border-[#dbe7ec] bg-white px-3 py-2 text-sm text-[#1d4758] outline-none focus:border-[#0ea5b7]"
          />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#8a9ba6]">Şık sayısı</label>
          <input
            type="number"
            min={2}
            max={8}
            value={material.optionCount || 4}
            onChange={(event) => {
              const optionCount = Math.max(2, Math.min(8, Number(event.target.value) || 4));
              const questionCount = material.questionCount || 1;
              updateVisualMaterial(material.draftId, {
                optionCount,
                correctAnswers: normalizeCorrectAnswers(questionCount, optionCount, material.correctAnswers),
              });
            }}
            className="mt-1 w-full rounded-xl border border-[#dbe7ec] bg-white px-3 py-2 text-sm text-[#1d4758] outline-none focus:border-[#0ea5b7]"
          />
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: material.questionCount || 1 }).map((_, questionIndex) => (
          <div key={`${material.draftId}_answer_${questionIndex}`} className="rounded-xl bg-white p-2">
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#8a9ba6]">
              {questionIndex + 1}. doğru
            </label>
            <select
              value={material.correctAnswers?.[questionIndex] ?? ''}
              onChange={(event) => {
                const nextAnswers = normalizeCorrectAnswers(material.questionCount || 1, material.optionCount || 4, material.correctAnswers);
                nextAnswers[questionIndex] = event.target.value === '' ? null : Number(event.target.value);
                updateVisualMaterial(material.draftId, { correctAnswers: nextAnswers });
              }}
              className="mt-1 w-full rounded-lg border border-[#dbe7ec] bg-white px-2 py-2 text-sm font-bold text-[#174e67] outline-none focus:border-[#0ea5b7]"
            >
              <option value="">Seç</option>
              {Array.from({ length: material.optionCount || 4 }).map((__, optionIndex) => (
                <option key={optionIndex} value={optionIndex}>
                  {String.fromCharCode(65 + optionIndex)}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );

  const updateVisualMaterial = (draftId: string, updates: Partial<VisualMaterialDraft>) => {
    setVisualMaterials((prev) => prev.map((item) => (
      item.draftId === draftId ? { ...item, ...updates } : item
    )));
  };

  const handleRotate = async (draftId: string, direction: 'left' | 'right') => {
    const material = visualMaterials.find((m) => m.draftId === draftId);
    if (!material || rotatingIds.has(draftId)) return;
    const source: File | string | null = material.file || material.previewUrl || null;
    if (!source) return;
    const filename = material.file?.name || 'gorsel.jpg';
    const deg = direction === 'right' ? 90 : 270;
    setRotatingIds((prev) => new Set(prev).add(draftId));
    try {
      const rotated = await rotateFileByDeg(source, deg, filename);
      const newPreviewUrl = URL.createObjectURL(rotated);
      setVisualMaterials((prev) => prev.map((m) => {
        if (m.draftId !== draftId) return m;
        if (m.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(m.previewUrl);
        return { ...m, file: rotated, previewUrl: newPreviewUrl };
      }));
    } catch {
      setError(`"${material.title || 'Görsel'}" döndürülemedi. Görseli yeniden yükleyip tekrar deneyin.`);
    } finally {
      setRotatingIds((prev) => { const s = new Set(prev); s.delete(draftId); return s; });
    }
  };

  const removeVisualMaterial = (draftId: string) => {
    setVisualMaterials((prev) => prev.filter((item) => item.draftId !== draftId));
  };

  const moveVisualMaterial = (draftId: string, direction: -1 | 1) => {
    setVisualMaterials((prev) => {
      const currentIndex = prev.findIndex((item) => item.draftId === draftId);
      const nextIndex = currentIndex + direction;
      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(currentIndex, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!title.trim()) return setError('Ders başlığı zorunludur');
    if (transcriptLength < 30) return setError('Transkript çok kısa görünüyor (en az 30 karakter)');
    const missingVisualInfo = visualMaterials.find((item) => !item.title.trim() || !item.pageLabel.trim());
    if (missingVisualInfo) return setError('Ders içi görsellerde ad ve sayfa/ad etiketi zorunludur');
    const incompleteQuestionVisual = visualMaterials.find((item) => (
      (getVisualCategory(item) === 'question' || (getVisualCategory(item) === 'measurement_text' && getMeasurementTextRole(item) === 'question')) &&
      normalizeCorrectAnswers(item.questionCount || 1, item.optionCount || 4, item.correctAnswers).some((answer) => answer === null)
    ));
    if (incompleteQuestionVisual) return setError('Soru sayfalarında tüm doğru cevaplar işaretlenmelidir');

    const payload: Partial<CreateLessonPayload> = {
      title: title.trim(),
      description: description.trim(),
      voice,
      rawTranscript: transcript.trim(),
      askSegmentQuizzes,
      visualMaterials: visualMaterials.map((item, index) => ({
        id: item.id,
        title: item.title.trim(),
        pageLabel: item.pageLabel.trim(),
        note: item.note?.trim() || '',
        order: index,
        media: item.media || null,
        file: item.file || null,
        visualCategory: getVisualCategory(item),
        measurementTextRole: getVisualCategory(item) === 'measurement_text' ? getMeasurementTextRole(item) : '',
        isQuestionVisual: getVisualCategory(item) === 'question',
        trackTime: getVisualCategory(item) === 'reading' ? item.trackTime !== false : false,
        questionCount: getVisualCategory(item) === 'question' || (getVisualCategory(item) === 'measurement_text' && getMeasurementTextRole(item) === 'question') ? item.questionCount || 1 : 0,
        optionCount: getVisualCategory(item) === 'question' || (getVisualCategory(item) === 'measurement_text' && getMeasurementTextRole(item) === 'question') ? item.optionCount || 4 : 0,
        correctAnswers: getVisualCategory(item) === 'question' || (getVisualCategory(item) === 'measurement_text' && getMeasurementTextRole(item) === 'question')
          ? normalizeCorrectAnswers(item.questionCount || 1, item.optionCount || 4, item.correctAnswers)
          : [],
        memoryPegCount: getVisualCategory(item) === 'memory_pegs' ? item.memoryPegCount || 0 : 0,
        memoryPegLabels: getVisualCategory(item) === 'memory_pegs' ? item.memoryPegLabels || [] : [],
        numberTrackingCount: getVisualCategory(item) === 'number_tracking' ? item.numberTrackingCount || 0 : 0,
        numberTrackingDurationSec: getVisualCategory(item) === 'number_tracking' ? item.numberTrackingDurationSec || 60 : 60,
        numberTrackingLineXPercent: getVisualCategory(item) === 'number_tracking' ? item.numberTrackingLineXPercent ?? 50 : 50,
        numberTrackingItems: getVisualCategory(item) === 'number_tracking' ? item.numberTrackingItems || [] : [],
        balloonReadingItems: getVisualCategory(item) === 'balloon_reading' ? item.balloonReadingItems || [] : [],
        balloonReadingDurationSec: getVisualCategory(item) === 'balloon_reading' ? item.balloonReadingDurationSec || 180 : 180,
        starReadingDurationSec: getVisualCategory(item) === 'star_reading' ? item.starReadingDurationSec || 120 : 120,
        gapReadingDurationSec: getVisualCategory(item) === 'gap_reading' ? item.gapReadingDurationSec || 120 : 120,
        eyeExerciseMode: ['horizontal_zigzag', 'three_column_grid'].includes(getVisualCategory(item)) ? getVisualCategory(item) : '',
        eyeExerciseDurationSec: ['horizontal_zigzag', 'three_column_grid'].includes(getVisualCategory(item)) ? item.eyeExerciseDurationSec || 120 : 0,
        metronomeBpm: ['horizontal_zigzag', 'three_column_grid'].includes(getVisualCategory(item)) ? item.metronomeBpm || 120 : 0,
      })),
    };
    if (coverFile) payload.cover = coverFile;
    if (!isEdit && courseId) payload.courseId = courseId;

    setSubmitting(true);
    try {
      const result = isEdit && initialLesson
        ? await TrainingApi.update(initialLesson.id, payload)
        : await TrainingApi.create(payload as CreateLessonPayload);
      onSaved(result);
    } catch (err: any) {
      setError(err?.message || 'Kaydetme başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-[#0b1f29]/55 backdrop-blur-sm sm:items-center sm:p-6">
      <div className="flex h-full w-full max-w-5xl flex-col overflow-hidden bg-white shadow-[0_30px_80px_rgba(11,31,41,0.25)] sm:h-auto sm:max-h-[90vh] sm:rounded-3xl">
        <div className="flex items-center justify-between border-b border-[#ecf2f5] px-6 py-4">
          <div>
            <h3 className="text-lg font-extrabold tracking-tight text-[#174e67]">
              {isEdit ? 'Eğitimi düzenle' : 'Yeni eğitim oluştur'}
            </h3>
            <p className="mt-1 text-xs text-[#67808c]">
              Transkripti ve ders içi görselleri kaydettiğinde Gemini metni temizler, görsel checkpointleri çıkarır ve sesi üretir.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-[#67808c] transition hover:bg-[#f4f7f8] hover:text-[#174e67]"
            aria-label="Kapat"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-y-auto px-6 py-6">
          <div className="flex flex-col gap-5 lg:flex-row">
            <div className="flex flex-col gap-3 lg:w-56">
              <label className="text-xs font-bold uppercase tracking-wide text-[#67808c]">Kapak görseli</label>
              <label className="group flex aspect-square cursor-pointer items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-[#dbe7ec] bg-[#f7fbfc] transition hover:border-[#0ea5b7]">
                {coverPreview ? (
                  <img src={coverPreview} alt="Kapak önizleme" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-[#67808c]">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="m21 15-5-5L5 21"/><circle cx="9" cy="9" r="2"/></svg>
                    <span className="text-xs">Görsel yükle</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => setCoverFile(event.target.files?.[0] || null)}
                />
              </label>
            </div>

            <div className="flex-1 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#67808c]">Başlık</label>
                <input
                  type="text"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Örn. Hızlı okumada göz atlama tekniği"
                  className="mt-1 w-full rounded-2xl border border-[#dbe7ec] bg-white px-4 py-3 text-sm text-[#1d4758] outline-none focus:border-[#0ea5b7]"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#67808c]">Kısa açıklama</label>
                <input
                  type="text"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Öğrencinin dersten alacağı tek cümlelik kazanım"
                  className="mt-1 w-full rounded-2xl border border-[#dbe7ec] bg-white px-4 py-3 text-sm text-[#1d4758] outline-none focus:border-[#0ea5b7]"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#67808c]">Eğitmen sesi</label>
                <select
                  value={voice}
                  onChange={(event) => setVoice(event.target.value)}
                  className="mt-1 w-full rounded-2xl border border-[#dbe7ec] bg-white px-4 py-3 text-sm text-[#1d4758] outline-none focus:border-[#0ea5b7]"
                >
                  {VOICE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-start gap-3 rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] px-4 py-3">
                <input
                  type="checkbox"
                  checked={askSegmentQuizzes}
                  onChange={(event) => setAskSegmentQuizzes(event.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#0ea5b7]"
                />
                <span>
                  <span className="block text-sm font-extrabold text-[#174e67]">Bölüm sonu mini soru sorulsun</span>
                  <span className="mt-1 block text-xs leading-5 text-[#67808c]">
                    Kapalıysa ders sadece anlatım, görsel checkpoint ve soru görseli akışıyla ilerler.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div className="mt-6 rounded-3xl border border-[#dbe7ec] bg-[#f7fbfc] p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#67808c]">Ders içi görseller</label>
                <p className="mt-1 text-xs text-[#67808c]">
                  Sayfa/ad etiketi transcriptte geçen ifade ile eşleşmelidir.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleAddHorizontalZigzagExercise}
                  className="inline-flex items-center justify-center rounded-2xl bg-[#eef6f8] px-4 py-2.5 text-sm font-bold text-[#174e67] shadow-sm transition hover:bg-[#dff0f3]"
                >
                  Yatay Zikzak ekle
                </button>
                <button
                  type="button"
                  onClick={handleAddThreeColumnGridExercise}
                  className="inline-flex items-center justify-center rounded-2xl bg-[#eef6f8] px-4 py-2.5 text-sm font-bold text-[#174e67] shadow-sm transition hover:bg-[#dff0f3]"
                >
                  3 Sütun Grid ekle
                </button>
                <label className="inline-flex cursor-pointer items-center justify-center rounded-2xl bg-white px-4 py-2.5 text-sm font-bold text-[#174e67] shadow-sm transition hover:bg-[#eef6f8]">
                  Görsel ekle
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(event) => {
                      handleAddVisualFiles(event.target.files);
                      event.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>

            {visualMaterials.length ? (
              <div className="mt-4 grid gap-3">
                {visualMaterials.map((material, index) => (
                  <div key={material.draftId} className="grid gap-3 rounded-2xl border border-[#dbe7ec] bg-white p-3 lg:grid-cols-[96px,1fr,150px,1.1fr,auto] lg:items-start">
                    <div className="relative h-24 overflow-hidden rounded-xl bg-[#eef6f8]">
                      {material.previewUrl ? (
                        <img src={material.previewUrl} alt={material.title || 'Ders içi görsel'} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[#67808c]">
                          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="m21 15-5-5L5 21"/><circle cx="9" cy="9" r="2"/></svg>
                        </div>
                      )}
                      {material.previewUrl ? (
                        <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-gradient-to-t from-black/60 to-transparent px-1 pb-1 pt-5">
                          <button
                            type="button"
                            title="Sola döndür"
                            disabled={rotatingIds.has(material.draftId)}
                            onClick={() => handleRotate(material.draftId, 'left')}
                            className="flex h-6 w-6 items-center justify-center rounded-md bg-white/90 text-[#174e67] shadow transition hover:bg-white disabled:opacity-40"
                          >
                            {rotatingIds.has(material.draftId) ? (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                            ) : (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
                            )}
                          </button>
                          <button
                            type="button"
                            title="Sağa döndür"
                            disabled={rotatingIds.has(material.draftId)}
                            onClick={() => handleRotate(material.draftId, 'right')}
                            className="flex h-6 w-6 items-center justify-center rounded-md bg-white/90 text-[#174e67] shadow transition hover:bg-white disabled:opacity-40"
                          >
                            {rotatingIds.has(material.draftId) ? (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                            ) : (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M21 3v5h-5"/></svg>
                            )}
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wide text-[#8a9ba6]">Ad</label>
                      <input
                        type="text"
                        value={material.title}
                        onChange={(event) => updateVisualMaterial(material.draftId, { title: event.target.value })}
                        placeholder="Örn. Göz sekmeleri tablosu"
                        className="mt-1 w-full rounded-xl border border-[#dbe7ec] bg-white px-3 py-2 text-sm text-[#1d4758] outline-none focus:border-[#0ea5b7]"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wide text-[#8a9ba6]">Sayfa/ad</label>
                      <input
                        type="text"
                        value={material.pageLabel}
                        onChange={(event) => updateVisualMaterial(material.draftId, { pageLabel: event.target.value })}
                        placeholder="Örn. 5. sayfa"
                        className="mt-1 w-full rounded-xl border border-[#dbe7ec] bg-white px-3 py-2 text-sm text-[#1d4758] outline-none focus:border-[#0ea5b7]"
                      />
                    </div>

                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wide text-[#8a9ba6]">Not</label>
                      <input
                        type="text"
                        value={material.note || ''}
                        onChange={(event) => updateVisualMaterial(material.draftId, { note: event.target.value })}
                        placeholder="Örn. Sağ üstteki paragraf okutulacak"
                        className="mt-1 w-full rounded-xl border border-[#dbe7ec] bg-white px-3 py-2 text-sm text-[#1d4758] outline-none focus:border-[#0ea5b7]"
                      />
                    </div>

                    <div className="rounded-2xl border border-[#ecf2f5] bg-[#f7fbfc] p-3 lg:col-span-4 lg:col-start-2">
                      <div className="grid gap-3 sm:grid-cols-[220px,1fr] sm:items-end">
                        <div>
                          <label className="text-[11px] font-bold uppercase tracking-wide text-[#8a9ba6]">Kategori</label>
                          <select
                            value={getVisualCategory(material)}
                            onChange={(event) => {
                              const visualCategory = event.target.value as VisualCategory;
                              updateVisualMaterial(material.draftId, {
                                visualCategory,
                                measurementTextRole: visualCategory === 'measurement_text' ? getMeasurementTextRole(material) : 'reading',
                                isQuestionVisual: visualCategory === 'question',
                                trackTime: visualCategory === 'reading' ? material.trackTime !== false : false,
                                correctAnswers: normalizeCorrectAnswers(material.questionCount || 4, material.optionCount || 4, material.correctAnswers),
                                memoryPegCount: visualCategory === 'memory_pegs' ? material.memoryPegCount || 0 : 0,
                                memoryPegLabels: visualCategory === 'memory_pegs' ? material.memoryPegLabels || [] : [],
                                numberTrackingCount: visualCategory === 'number_tracking' ? material.numberTrackingCount || 0 : 0,
                                numberTrackingDurationSec: visualCategory === 'number_tracking' ? material.numberTrackingDurationSec || 60 : 60,
                                numberTrackingLineXPercent: visualCategory === 'number_tracking' ? material.numberTrackingLineXPercent ?? 50 : 50,
                                numberTrackingItems: visualCategory === 'number_tracking' ? material.numberTrackingItems || [] : [],
                                balloonReadingItems: visualCategory === 'balloon_reading' ? material.balloonReadingItems || [] : [],
                                balloonReadingDurationSec: visualCategory === 'balloon_reading' ? material.balloonReadingDurationSec || 180 : 180,
                                starReadingDurationSec: visualCategory === 'star_reading' ? material.starReadingDurationSec || 120 : 120,
                                gapReadingDurationSec: visualCategory === 'gap_reading' ? material.gapReadingDurationSec || 120 : 120,
                                eyeExerciseMode: ['horizontal_zigzag', 'three_column_grid'].includes(visualCategory) ? visualCategory : '',
                                eyeExerciseDurationSec: ['horizontal_zigzag', 'three_column_grid'].includes(visualCategory) ? material.eyeExerciseDurationSec || 120 : 0,
                                metronomeBpm: ['horizontal_zigzag', 'three_column_grid'].includes(visualCategory) ? material.metronomeBpm || 120 : 0,
                              });
                            }}
                            className="mt-1 w-full rounded-xl border border-[#dbe7ec] bg-white px-3 py-2 text-sm font-bold text-[#174e67] outline-none focus:border-[#0ea5b7]"
                          >
                            <option value="reading">Okuma görseli</option>
                            <option value="question">Soru</option>
                            <option value="memory_pegs">Hafıza Çivileri</option>
                            <option value="number_tracking">Numara Yakalama</option>
                            <option value="word_alternatives">Kelime Alternatifleri</option>
                            <option value="balloon_reading">Balon Okuma</option>
                            <option value="star_reading">Yıldız Okuma</option>
                            <option value="gap_reading">Boşluk Okuma</option>
                            <option value="measurement_text">Ölçüm Metni</option>
                            <option value="horizontal_zigzag">Yatay Zikzak</option>
                            <option value="three_column_grid">3 Sütun Grid</option>
                          </select>
                        </div>
                        {getVisualCategory(material) === 'reading' ? (
                          <label className="inline-flex items-center gap-2 text-sm font-bold text-[#174e67]">
                            <input
                              type="checkbox"
                              checked={material.trackTime !== false}
                              onChange={(event) => updateVisualMaterial(material.draftId, { trackTime: event.target.checked })}
                              className="h-4 w-4 accent-[#0ea5b7]"
                            />
                            Okuma süresini takip et
                          </label>
                        ) : null}
                      </div>

                      {getVisualCategory(material) === 'question' ? renderQuestionAnswerEditor(material) : null}
                      {getVisualCategory(material) === 'memory_pegs' ? (
                        <div className="mt-3 rounded-2xl bg-white px-4 py-3 text-sm font-semibold leading-5 text-[#174e67]">
                          Gemini eğitim oluşturulurken yüklenen görseldeki sayı/etiketleri tespit eder. Öğrenci bu sıraya göre zihninde oluşan anlamları yazar; doğru-yanlış kontrolü yapılmaz.
                        </div>
                      ) : null}
                      {getVisualCategory(material) === 'number_tracking' ? (
                        <div className="mt-3 rounded-2xl bg-white px-4 py-3 text-sm font-semibold leading-5 text-[#174e67]">
                          Gemini eğitim oluşturulurken görseldeki sayıların konumlarını tespit eder. Öğrenci 60 saniye boyunca 1'den son sayıya kadar sırayla tıklar; ikinci turda görselin ortasından kırmızı çizgi çekilerek tekrar çalışır.
                        </div>
                      ) : null}
                      {getVisualCategory(material) === 'word_alternatives' ? (
                        <div className="mt-3 rounded-2xl bg-white px-4 py-3 text-sm font-semibold leading-5 text-[#174e67]">
                          Boşluklu metin ve anahtar kelime görseli olarak işlenir. Öğrenci uygun kelime alternatiflerini kendi cevap alanına yazar; doğru-yanlış kontrolü yapılmaz.
                        </div>
                      ) : null}
                      {getVisualCategory(material) === 'balloon_reading' ? (
                        <div className="mt-3 rounded-2xl bg-white px-4 py-3 text-sm font-semibold leading-5 text-[#174e67]">
                          Gemini eğitim oluşturulurken mavi balon/öbek kutularının konumlarını çıkarır. Eğitim sırasında her tıkta sıradaki öbek veya satır mavi vurgu ile takip edilir.
                        </div>
                      ) : null}
                      {getVisualCategory(material) === 'star_reading' ? (
                        <div className="mt-3 rounded-2xl bg-white px-4 py-3 text-sm font-semibold leading-5 text-[#174e67]">
                          Tek görsel kullanılır. Eğitim sırasında öğrenci metronom ritmine göre görseldeki yıldızları takip ederek ikili kelime öbeklerini okur; uygulama ek çizim veya OCR yapmaz.
                        </div>
                      ) : null}
                      {getVisualCategory(material) === 'gap_reading' ? (
                        <div className="mt-3 rounded-2xl bg-white px-4 py-3 text-sm font-semibold leading-5 text-[#174e67]">
                          Boşluk Okuma görseli yüklenir. Eğitim sırasında öğrenci kalem/parmağını boşluklara koyarak metronom ritmiyle üçlü kelime gruplarını okur; uygulama ek çizim veya OCR yapmaz.
                        </div>
                      ) : null}
                      {getVisualCategory(material) === 'measurement_text' ? (
                        <div className="mt-3 grid gap-3">
                          <div className="rounded-2xl bg-white px-4 py-3">
                            <label className="text-[11px] font-bold uppercase tracking-wide text-[#8a9ba6]">Ölçüm sayfası türü</label>
                            <select
                              value={getMeasurementTextRole(material)}
                              onChange={(event) => {
                                const measurementTextRole = event.target.value as 'reading' | 'question';
                                updateVisualMaterial(material.draftId, {
                                  measurementTextRole,
                                  questionCount: measurementTextRole === 'question' ? material.questionCount || 8 : 0,
                                  optionCount: measurementTextRole === 'question' ? material.optionCount || 4 : 0,
                                  correctAnswers: measurementTextRole === 'question'
                                    ? normalizeCorrectAnswers(material.questionCount || 8, material.optionCount || 4, material.correctAnswers)
                                    : [],
                                });
                              }}
                              className="mt-1 w-full rounded-xl border border-[#dbe7ec] bg-white px-3 py-2 text-sm font-bold text-[#174e67] outline-none focus:border-[#0ea5b7]"
                            >
                              <option value="reading">Okuma sayfası</option>
                              <option value="question">Soru sayfası</option>
                            </select>
                            <p className="mt-2 text-sm font-semibold leading-5 text-[#174e67]">
                              {getMeasurementTextRole(material) === 'question'
                                ? 'Bu görsel okuma bittikten sonra çözülecek soru sayfasıdır. Doğru cevapları aşağıdan işaretle.'
                                : 'Bu görsel süre ölçümü yapılacak okuma sayfasıdır. Öğrenci sayfayı bitirince süre otomatik kaydedilir.'}
                            </p>
                          </div>
                          {getMeasurementTextRole(material) === 'question' ? renderQuestionAnswerEditor(material) : null}
                        </div>
                      ) : null}
                      {getVisualCategory(material) === 'horizontal_zigzag' ? (
                        <div className="mt-3 rounded-2xl bg-white px-4 py-3 text-sm font-semibold leading-5 text-[#174e67]">
                          Görsel dosyası gerekmez. Eğitim sırasında sağ panelde yerleşik Yatay Zikzak açılır; transcriptteki 120, 150 ve 180 BPM çalışma komutları ayrı checkpointlere bağlanır.
                        </div>
                      ) : null}
                      {getVisualCategory(material) === 'three_column_grid' ? (
                        <div className="mt-3 rounded-2xl bg-white px-4 py-3 text-sm font-semibold leading-5 text-[#174e67]">
                          Görsel dosyası gerekmez. Eğitim sırasında sağ panelde 3×3 nokta grid açılır; 1'den 9'a sırayla metronomla göz egzersizi yapılır. Transcriptteki 120, 150 ve 180 BPM çalışma komutları ayrı checkpointlere bağlanır.
                        </div>
                      ) : null}
                    </div>

                    <div className="flex items-center justify-end gap-2 lg:flex-col">
                      <button
                        type="button"
                        onClick={() => moveVisualMaterial(material.draftId, -1)}
                        disabled={index === 0}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#eef6f8] text-[#174e67] transition hover:bg-[#dff0f3] disabled:cursor-not-allowed disabled:text-[#9fb3bc]"
                        aria-label="Yukarı taşı"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => moveVisualMaterial(material.draftId, 1)}
                        disabled={index === visualMaterials.length - 1}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#eef6f8] text-[#174e67] transition hover:bg-[#dff0f3] disabled:cursor-not-allowed disabled:text-[#9fb3bc]"
                        aria-label="Aşağı taşı"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => removeVisualMaterial(material.draftId)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#fef4f4] text-[#a04c4c] transition hover:bg-[#fde0e0]"
                        aria-label="Görseli sil"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-dashed border-[#cfe2e8] bg-white px-4 py-6 text-center text-sm font-semibold text-[#67808c]">
                Ders içi görsel eklenmedi.
              </div>
            )}
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wide text-[#67808c]">Ders transkripti</label>
              <span className="text-[11px] text-[#8a9ba6]">{transcriptLength.toLocaleString('tr-TR')} karakter</span>
            </div>
            <textarea
              value={transcript}
              onChange={(event) => setTranscript(event.target.value)}
              rows={14}
              placeholder="Dersin tam transkriptini buraya yapıştır. Gemini, abone ol/takip et gibi gereksiz pasajları otomatik temizleyecek ve eğitmen sesine dönüştürecek."
              className="mt-1 w-full resize-y rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] px-4 py-3 text-sm leading-6 text-[#1d4758] outline-none focus:border-[#0ea5b7] focus:bg-white"
            />
          </div>

          {error ? (
            <p className="mt-4 rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm text-[#a04c4c]">{error}</p>
          ) : null}

          <div className="mt-6 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-[#dbe7ec] bg-white px-5 py-3 text-sm font-bold text-[#67808c] hover:text-[#174e67]"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-2xl bg-gradient-to-br from-[#0ea5b7] to-[#174e67] px-5 py-3 text-sm font-bold text-white shadow-[0_14px_40px_rgba(14,165,183,0.35)] transition disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting ? 'Kaydediliyor…' : isEdit ? 'Değişiklikleri kaydet' : 'Eğitimi oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LessonAdminDialog;
